import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { changePassword } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Gear, LockKey, Clock, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, isAdmin } from "@/lib/access";
import { UserManagement } from "@/components/settings/UserManagement";
import { StockAlertSettings } from "@/components/settings/StockAlertSettings";
import { GoogleAuthSettings } from "@/components/settings/GoogleAuthSettings";

const API = `${window.location.origin}/api`;

export default function SettingsPage() {
  const admin = isAdmin(useAuth());
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [loading, setLoading] = useState(false);
  const [whForm, setWhForm] = useState({ hours_per_day: 8, office_start: "09:00", late_time: "09:30" });
  const [whLoading, setWhLoading] = useState(false);

  const fetchWorkingHours = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/working-hours`);
      setWhForm(res.data);
    } catch (err) { toast.error("Failed to load working hours"); }
  }, []);

  useEffect(() => { if (admin) fetchWorkingHours(); }, [admin, fetchWorkingHours]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.current_password || !pwForm.new_password) { toast.error("Fill all fields"); return; }
    if (pwForm.new_password.length < 6) { toast.error("New password must be 6+ characters"); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error("Passwords don't match"); return; }
    try {
      setLoading(true);
      await changePassword(pwForm.current_password, pwForm.new_password);
      toast.success("Password changed successfully!");
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally { setLoading(false); }
  };

  const handleSaveWorkingHours = async () => {
    setWhLoading(true);
    try {
      await axios.post(`${API}/settings/working-hours`, whForm);
      toast.success("Working hours configuration saved!");
    } catch (err) { toast.error("Failed to save"); }
    finally { setWhLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Gupter', serif" }}>Settings</h1>
        <p className="text-sm text-gray-500 mt-1">{admin ? "Manage users, your account and working hours" : "Manage your account"}</p>
      </div>

      {admin && <UserManagement />}

      {admin && <GoogleAuthSettings />}

      {admin && <StockAlertSettings />}

      {/* Working Hours Configuration */}
      {admin && (
      <Card className="p-6 border border-gray-200 bg-white" data-testid="working-hours-config">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Clock size={16} className="text-indigo-500" weight="bold" /> Working Hours Configuration
        </h3>
        <p className="text-xs text-gray-500 mb-4">Set office hours, start time, and late entry threshold. These settings are used for attendance late marking and permission calculations.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-lg">
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-1 block">Hours Per Day</Label>
            <Input type="number" min="1" max="24" step="0.5" value={whForm.hours_per_day} onChange={(e) => setWhForm(p => ({ ...p, hours_per_day: parseFloat(e.target.value) || 8 }))} className="h-11" data-testid="wh-hours-per-day" />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-1 block">Office Start Time</Label>
            <Input type="time" value={whForm.office_start} onChange={(e) => setWhForm(p => ({ ...p, office_start: e.target.value }))} className="h-11" data-testid="wh-office-start" />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-1 block">Late After</Label>
            <Input type="time" value={whForm.late_time} onChange={(e) => setWhForm(p => ({ ...p, late_time: e.target.value }))} className="h-11" data-testid="wh-late-time" />
          </div>
        </div>
        <Button onClick={handleSaveWorkingHours} disabled={whLoading} className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-working-hours-btn">
          <Check size={16} className="mr-1" /> {whLoading ? "Saving..." : "Save Configuration"}
        </Button>
      </Card>
      )}

      {/* Change Password */}
      <Card className="p-6 border border-gray-200 bg-white">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <LockKey size={16} className="text-indigo-500" /> Change Password
        </h3>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-1 block">Current Password</Label>
            <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} placeholder="Enter current password" data-testid="current-password-input" />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-1 block">New Password</Label>
            <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} placeholder="Min 6 characters" data-testid="new-password-input" />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-1 block">Confirm New Password</Label>
            <Input type="password" value={pwForm.confirm_password} onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })} placeholder="Confirm new password" data-testid="confirm-password-input" />
          </div>
          <Button type="submit" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="change-password-btn">
            {loading ? "Changing..." : "Change Password"}
          </Button>
        </form>
      </Card>

      {/* App Info */}
      <Card className="p-6 border border-gray-200 bg-white">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Gear size={16} className="text-indigo-500" /> Application Info
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="font-semibold text-gray-900 mb-1 text-sm">Version</p>
            <p className="text-sm text-gray-600">Huma Spices CRM v2.1</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="font-semibold text-gray-900 mb-1 text-sm">Environment</p>
            <p className="text-sm text-gray-600">Production</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
